import BTDropDown from "@common-components/atoms/drop-down/bt-drop-down";
import { UserMessage } from '@bt-common/schemas/bt/user-message';

import { Box, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";

import type { NavigateAction, ToolbarProps, View } from "react-big-calendar";
import { NavigateNext, NavigateBefore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

// eslint-disable-next-line import/no-webpack-loader-syntax
import "!style-loader!css-loader!./custom-toolbar.css";
// Note: custom-toolbar.css is also imported in css-modules-entry.js for shadow DOM injection
import moment from "moment";
import { CSSSelectorObjectOrCssVariables } from "@mui/system";
import ToggleCalendarAgenda from "@common/components/toggle-calendar-agenda/toggle-calendar-agenda";
import RowStack from "@/common/components/common/row-stack/RowStack";
import { useCallback } from "react";
import { getMomentLocale } from "@common-config/moment-locales";
import { useIsMobile } from "@common/hooks/use-is-mobile";

interface P {
  toolbarProps: ToolbarProps<UserMessage, any>;
  calendarOnNavigate?: (newDate: Date, view: View, action?: NavigateAction) => void;
}

function CustomToolbar(props: P) {
  const { t, i18n } = useTranslation(namespace);
  const isMobile = useIsMobile();
  const { view, onNavigate, date } = props.toolbarProps;
  const calendarOnNavigate = props.calendarOnNavigate;
  const activeLanguage = i18n.resolvedLanguage || i18n.language || 'en';
  const momentLocale = getMomentLocale(activeLanguage);
  const isRtl = i18n.dir(activeLanguage) === 'rtl';
  const viewLabel = t(`calendar.${view}`, { defaultValue: view });
  const previousAction: NavigateAction = isRtl ? "NEXT" : "PREV";
  const nextAction: NavigateAction = isRtl ? "PREV" : "NEXT";

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    props.toolbarProps.onView(event.target.value as View);
  };

  // Manually calculate new date to bypass any react-big-calendar restrictions
  // This ensures backward navigation works even to past dates
  const handleNavigate = useCallback((action: NavigateAction, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    const currentDate = date || new Date();
    let newDate: Date;
    
    switch (action) {
      case 'PREV':
        if (view === 'month') {
          newDate = moment(currentDate).subtract(1, 'month').toDate();
        } else if (view === 'week') {
          newDate = moment(currentDate).subtract(1, 'week').toDate();
        } else if (view === 'day') {
          newDate = moment(currentDate).subtract(1, 'day').toDate();
        } else {
          newDate = moment(currentDate).subtract(1, 'month').toDate();
        }
        break;
      case 'NEXT':
        if (view === 'month') {
          newDate = moment(currentDate).add(1, 'month').toDate();
        } else if (view === 'week') {
          newDate = moment(currentDate).add(1, 'week').toDate();
        } else if (view === 'day') {
          newDate = moment(currentDate).add(1, 'day').toDate();
        } else {
          newDate = moment(currentDate).add(1, 'month').toDate();
        }
        break;
      case 'TODAY':
        newDate = new Date();
        break;
      default:
        // Fallback to react-big-calendar's internal navigation
        onNavigate(action);
        return;
    }
    
    // Use calendar's onNavigate callback if available, otherwise fallback to toolbar's onNavigate
    if (calendarOnNavigate) {
      // Call calendar's onNavigate with the calculated date directly - bypasses any restrictions
      calendarOnNavigate(newDate, view, action);
    } else {
      // Fallback to react-big-calendar's internal navigation (may have restrictions)
      onNavigate(action);
    }
  }, [onNavigate, calendarOnNavigate, date, view]);

  const hoverSX = {
    cursor: "pointer",
    backgroundColor: 'var(--color-surface-muted)',
    color: 'var(--color-text)',
  } as CSSSelectorObjectOrCssVariables<{}>;

  const arrowBoxSX = {
    "&:hover": hoverSX,
    display: "flex",
    cursor: "pointer",
    userSelect: "none",
    WebkitUserSelect: "none",
    msUserSelect: "none",
    touchAction: "manipulation",
  } as CSSSelectorObjectOrCssVariables<{}>;

  return (
    <RowStack
      sx={{
        margin: '7px 0',
        zIndex: 1,
        gap: isMobile ? 1 : 2,
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        rowGap: isMobile ? 1 : 0,
      }}
      justifyContent="space-between"
    >
      <RowStack>
        {/* 3. Navigation arrows */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <Tooltip title={`${t('calendar.previous')} ${viewLabel}`}>
            <Box
              sx={arrowBoxSX}
              onClick={(e) => {
                handleNavigate(previousAction, e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {isRtl ? (
                <NavigateNext sx={{ color: 'var(--color-text)', fontSize: "20px", pointerEvents: "none" }} />
              ) : (
                <NavigateBefore sx={{ color: 'var(--color-text)', fontSize: "20px", pointerEvents: "none" }} />
              )}
            </Box>
          </Tooltip>
          <Tooltip title={`${t('calendar.next')} ${viewLabel}`}>
            <Box
              sx={arrowBoxSX}
              onClick={(e) => {
                handleNavigate(nextAction, e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {isRtl ? (
                <NavigateBefore sx={{ color: 'var(--color-text)', fontSize: "20px", pointerEvents: "none" }} />
              ) : (
                <NavigateNext sx={{ color: 'var(--color-text)', fontSize: "20px", pointerEvents: "none" }} />
              )}
            </Box>
          </Tooltip>
        </Box>

        {/* 2. Date text */}
        <Box sx={{ fontSize: isMobile ? "14px" : "16px", fontWeight: 600, marginInlineStart: "10px", color: 'var(--color-text)', minWidth: isMobile ? 'auto' : '120px' }}>{props.toolbarProps.label}</Box>
      </RowStack>

      {/* 4. Calendar/Agenda toggle */}
      <Box sx={{ mx: isMobile ? 0.5 : 2 }}>
        <Box sx={{
          borderRadius: '6px',
          padding: '4px',
          display: 'flex',
          gap: '4px',
          backgroundColor: '0 0% 100%',
        }}>
          <ToggleCalendarAgenda />
        </Box>
      </Box>


      <RowStack>
        {/* 5. Month dropdown */}
        <Box sx={{ maxWidth: "fit-content" }}>
          <BTDropDown
            value={view}
            onChange={handleChange}
            autoWidth
            className="cal-toolbar-select"
            sx={{
              border: 'none!important',
              fontWeight: 600,
              fontSize: "14px",
              ".MuiOutlinedInput-input": {
                padding: 0,
                pl: 1,
                display: "flex",
                alignItems: "center",
              },
              ".MuiSelect-select": {
                color: 'var(--color-text)',
                height: "36px",
              },
              ".MuiSvgIcon-root": {
                color: 'var(--color-text)',
              },
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent !important",
              },
            }}
          >
            <MenuItem value={"month"}>{t('calendar.month')}</MenuItem>
            <MenuItem value={"week"}>{t('calendar.week')}</MenuItem>
            <MenuItem value={"day"}>{t('calendar.day')}</MenuItem>
          </BTDropDown>

        </Box>

        {/* 1. Today button */}
        <Tooltip title={moment().locale(momentLocale).format("dddd, DD MMMM")}>
          <Box 
            onClick={(e) => handleNavigate("TODAY", e)} 
            className="cal-toolbar-today" 
            sx={{
              border: 'none!important',
              marginTop: '0!important',
              marginInlineEnd: '0!important',
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {t('calendar.today')}
          </Box>
        </Tooltip>
      </RowStack>




    </RowStack>
  );
}

export default CustomToolbar;
